import React, { useMemo, useState, useEffect } from 'react';
import { X, PersonCircle, Pencil, Calendar2Date } from 'react-bootstrap-icons';
import { Badge, Button, Col, Row } from 'react-bootstrap';
import useLocalization from '../../hooks/useLocalization';
import './styles.scss';
import AppModal from '../common/AppModal';
import CustomMultiSelect from '../common/CustomMultiSelect';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { profileAssignmentStatusOptions } from '../../helpers/constants';
import { snakeCase } from 'lodash';

const CustomerSelector = ({ apartmentExtraInfo, onRemoveCustomer, setApartmentExtraInfo }) => {
  const { translate } = useLocalization();
  const [profileAssignmentMeta, setProfileAssignmentMeta] = useState({
    profileAssignment: null,
    showModal: false
  });

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .customer-card:hover .action-buttons {
        opacity: 1 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleProfileUpdate = updatedProfileAssignment => {
    setApartmentExtraInfo(prev => ({
      ...prev,
      profileAssignments: prev.profileAssignments.map(pa => {
        if (pa?.customer?._id === updatedProfileAssignment?.customer?._id) {
          return { ...pa, ...updatedProfileAssignment };
        }
        return pa;
      })
    }));
  };

  const pKeyIndex = useMemo(() => {
    return customer => {
      if (!customer?.apartmentProfiles) return {};
      return customer.apartmentProfiles.reduce((acc, p, i) => {
        acc[p?._id] = `P${i + 1}`;
        return acc;
      }, {});
    };
  }, []);

  return (
    <>
      {apartmentExtraInfo.profileAssignments?.map(pa => {
        const statusOption = profileAssignmentStatusOptions.find(s => s.value === pa?.status);

        return (
          <div
            key={pa?._id}
            className="customer-card shadow-sm"
            style={{
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid #e0e0e0',
              backgroundColor: '#fff',
              minWidth: '220px'
            }}
          >
            <div
              className="card-header d-flex align-items-center justify-content-between p-2 position-relative"
              style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #e0e0e0'
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <div
                  className="customer-avatar d-flex align-items-center justify-content-center"
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: '#e9ecef'
                  }}
                >
                  <PersonCircle size={16} className="text-primary" />
                </div>
                <div>
                  <h6 className="mb-0 fw-bold small">{pa?.customer?.name || 'No name'}</h6>
                  <p className="mb-0 tiny">{pa?.customer?.email || ''}</p>
                </div>
              </div>
              <div
                className="action-buttons d-flex justify-content-end align-items-center gap-0 opacity-0 ms-2 me-0"
                style={{
                  transition: 'opacity 0.2s ease-in-out'
                }}
              >
                <Button
                  onClick={() => {
                    setProfileAssignmentMeta(prev => ({ ...prev, showModal: true, profileAssignment: pa }));
                  }}
                  size="sm"
                  variant="transparent"
                  className="d-flex align-items-center p-1 me-1"
                >
                  <Pencil size={12} />
                </Button>
                <Button
                  onClick={e => {
                    e.stopPropagation();
                    onRemoveCustomer(pa?.customer);
                  }}
                  size="sm"
                  variant="transparent"
                  className="d-flex align-items-center p-1 hover-danger text-danger me-0"
                >
                  <X size={18} />
                </Button>
              </div>
            </div>
            <div className="card-body d-flex align-items-center justify-content-between gap-3 p-2">
              <div className="d-flex gap-2 align-items-center">
                <Badge
                  size="sm"
                  bg={statusOption?.bg?.split(' ')[0] === 'secondary' ? 'secondary text-dark' : statusOption?.bg}
                  className="py-1 px-2"
                  style={{ fontSize: '0.75rem', fontWeight: '500' }}
                >
                  {translate(snakeCase(pa?.status))}
                </Badge>
                <Badge
                  size="sm"
                  bg="light"
                  text="dark"
                  className="py-1 px-2 border rounded-pill"
                  style={{ fontSize: '0.7rem', fontWeight: '500' }}
                >
                  {pKeyIndex(pa?.customer)[pa?.profile]}
                </Badge>
              </div>
              <div className="d-flex align-items-center gap-1">
                <Calendar2Date size={12} />
                <p className="mb-0 tiny pt-1">
                  {pa?.contractPreparationDate || pa?.assignmentDate
                    ? moment(pa?.contractPreparationDate || pa?.assignmentDate).format('DD/MM/YYYY')
                    : ''}
                </p>
              </div>
            </div>
          </div>
        );
      })}
      <ProfileAssignmentModal
        show={profileAssignmentMeta.showModal}
        onHide={() => setProfileAssignmentMeta(prev => ({ ...prev, showModal: false, profileAssignment: null }))}
        profileAssignment={profileAssignmentMeta.profileAssignment}
        onSave={handleProfileUpdate}
        pKeyIndex={pKeyIndex}
      />
    </>
  );
};

const ProfileAssignmentModal = ({ show, onHide, profileAssignment, onSave, pKeyIndex }) => {
  const { translate } = useLocalization();
  const [localProfileAssignment, setLocalProfileAssignment] = useState(null);

  useEffect(() => {
    if (profileAssignment) {
      setLocalProfileAssignment({ ...profileAssignment });
    }
  }, [profileAssignment]);

  const handleLocalStatusChange = newStatus => {
    setLocalProfileAssignment(prev => {
      const updated = { ...prev, status: newStatus };

      const statusAndDate = [
        {
          status: 'Assigned',
          date: 'assignmentDate'
        },
        {
          status: 'Advanced Check',
          date: 'advancedCheckDate'
        },
        {
          status: 'Preparing Contract',
          date: 'contractPreparationDate'
        },
        {
          status: "Didn't Close",
          date: 'didNotCloseDate'
        }
      ];

      statusAndDate.forEach(statusDate => {
        if (newStatus === statusDate.status && !prev[statusDate.date]) {
          updated[statusDate.date] = new Date();
        } else if (newStatus !== statusDate.status && !profileAssignment[statusDate.date]) {
          updated[statusDate.date] = null;
        }
      });

      return updated;
    });
  };

  const handleLocalDateChange = (fieldKey, newDate) => {
    if (!(newDate instanceof Date) || isNaN(newDate)) {
      console.error('Invalid date provided');
      return;
    }

    setLocalProfileAssignment(prev => ({
      ...prev,
      [fieldKey]: newDate
    }));
  };

  const handleSave = () => {
    onSave(localProfileAssignment);
    onHide();
  };

  if (!localProfileAssignment) return null;

  return (
    <AppModal show={show} onHide={onHide} title={'Edit Profile Assignment'}>
      <div className="px-2">
        <Row>
          <Col sm={12} md={6} lg={6} className="mb-2">
            <label className="fw-bold">Customer:</label>
            <h6>{localProfileAssignment?.customer?.name || localProfileAssignment?.customer?.email || 'No name'}</h6>
          </Col>

          <Col sm={12} md={6} lg={6} className="mb-2">
            <label className="fw-bold">Status:</label>
            <CustomMultiSelect
              items={profileAssignmentStatusOptions.map(s => ({
                label: translate(snakeCase(s.label)),
                value: s.value,
                hoverColor: s.hoverColor,
                backgroundColor: s.backgroundColor,
                textColor: 'black',
                selectedColor: s.selectedColor,
                selectedBgColor: s.selectedBgColor
              }))}
              selectedItems={[localProfileAssignment?.status]}
              onChange={val => handleLocalStatusChange(val)}
              isMulti={false}
              closeMenuOnSelect
            />
          </Col>
          {localProfileAssignment?.status ===
            profileAssignmentStatusOptions.find(s => s.label === "Didn't Close")?.value && (
            <Col sm={12} md={12} lg={12} className="mb-2">
              <label className="fw-bold">Reason:</label>
              <textarea
                className="form-control form-control-sm"
                rows={3}
                value={localProfileAssignment?.reason}
                onChange={e => {
                  setLocalProfileAssignment(prev => ({ ...prev, reason: e.target.value }));
                }}
              />
            </Col>
          )}

          <Col sm={12} md={6} lg={6} className="mb-2">
            <label className="fw-bold">Assignment Date:</label>
            <div className="position-relative w-100">
              <DatePicker
                selected={
                  localProfileAssignment?.assignmentDate ? new Date(localProfileAssignment.assignmentDate) : null
                }
                onChange={date => {
                  handleLocalDateChange('assignmentDate', date);
                }}
                isClearable={false}
                dateFormat="dd/MM/yyyy"
                wrapperClassName="w-100"
                className={`form-control form-control-sm py-0`}
                popperProps={{
                  strategy: 'fixed',
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: {
                        boundary: 'viewport'
                      }
                    }
                  ]
                }}
                popperPlacement="top-start"
                popperContainer={({ children }) => <div style={{ zIndex: 9999, position: 'relative' }}>{children}</div>}
              />
            </div>
          </Col>
          <Col sm={12} md={6} lg={6} className="mb-2">
            <label className="fw-bold">Advanced Check Date:</label>
            <div className="position-relative w-100">
              <DatePicker
                selected={
                  localProfileAssignment?.advancedCheckDate ? new Date(localProfileAssignment.advancedCheckDate) : null
                }
                onChange={date => {
                  handleLocalDateChange('advancedCheckDate', date);
                }}
                isClearable={false}
                dateFormat="dd/MM/yyyy"
                wrapperClassName="w-100"
                className={`form-control form-control-sm py-0`}
                popperProps={{
                  strategy: 'fixed',
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: {
                        boundary: 'viewport'
                      }
                    }
                  ]
                }}
                popperPlacement="top-start"
                popperContainer={({ children }) => <div style={{ zIndex: 9999, position: 'relative' }}>{children}</div>}
              />
            </div>
          </Col>
          <Col sm={12} md={6} lg={6} className="mb-2">
            <label className="fw-bold">Contract Preparation Date:</label>
            <div className="position-relative w-100">
              <DatePicker
                selected={
                  localProfileAssignment?.contractPreparationDate
                    ? new Date(localProfileAssignment.contractPreparationDate)
                    : null
                }
                onChange={date => {
                  handleLocalDateChange('contractPreparationDate', date);
                }}
                isClearable={false}
                dateFormat="dd/MM/yyyy"
                wrapperClassName="w-100"
                className={`form-control form-control-sm py-0`}
                popperProps={{
                  strategy: 'fixed',
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: {
                        boundary: 'viewport'
                      }
                    }
                  ]
                }}
                popperPlacement="top-start"
                popperContainer={({ children }) => <div style={{ zIndex: 9999, position: 'relative' }}>{children}</div>}
              />
            </div>
          </Col>

          <Col sm={12} md={6} lg={6} className="mb-2">
            <label className="fw-bold">Did Not Close Date:</label>
            <div className="position-relative w-100">
              <DatePicker
                selected={
                  localProfileAssignment?.didNotCloseDate ? new Date(localProfileAssignment.didNotCloseDate) : null
                }
                onChange={date => {
                  handleLocalDateChange('didNotCloseDate', date);
                }}
                isClearable={false}
                dateFormat="dd/MM/yyyy"
                wrapperClassName="w-100"
                className={`form-control form-control-sm py-0`}
                popperProps={{
                  strategy: 'fixed',
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: {
                        boundary: 'viewport'
                      }
                    }
                  ]
                }}
                popperPlacement="top-start"
                popperContainer={({ children }) => <div style={{ zIndex: 9999, position: 'relative' }}>{children}</div>}
              />
            </div>
          </Col>
          <Col sm={12} md={12} lg={12} className="mb-2">
            <label className="fw-bold">Profile:</label>

            <div className="d-flex flex-wrap gap-1">
              {localProfileAssignment?.customer?.apartmentProfiles?.length === 0 && (
                <p>{translate('no_profiles_found')}</p>
              )}
              {localProfileAssignment?.customer?.apartmentProfiles?.map(p => (
                <Button
                  variant={`${localProfileAssignment?.profile === p?._id ? 'dark' : 'outline-dark'}`}
                  size="sm"
                  className="rounded-1 smaller my-1 mx-1 px-3"
                  onClick={() => {
                    setLocalProfileAssignment(prev => ({
                      ...prev,
                      profile: p?._id
                    }));
                  }}
                >
                  {pKeyIndex(profileAssignment?.customer)[p?._id]}
                </Button>
              ))}
            </div>
          </Col>
        </Row>
        <div className="mt-2 d-flex justify-content-end align-items-center">
          <Button variant="success" size="sm" className="rounded-1 smaller my-1 text-white" onClick={handleSave}>
            {translate('save')}
          </Button>
        </div>
      </div>
    </AppModal>
  );
};

export default CustomerSelector;
